import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  message,
  Switch,
  Select,
  Tag,
  Tooltip,
  Upload,
  Card,
  Typography,
} from "antd";
import { useSelector } from "react-redux";
import { selectBaseUrl, selectProdTestUrl } from "../../redux/slices/envSlice";
import { fetchWellnessExecutive } from "./fetchWellnessExecutive";
import { EditOutlined, InfoCircleOutlined, UploadOutlined, PlusOutlined } from "@ant-design/icons";
import { fetchLab } from "../labPartner/FetchLab";
import SecureImage from "./SecureImage";
import {
  assign_lab_to_we,
  createExecutive,
  toggleExecutiveStatus,
  update_assign_lab_to_we,
  updateExecutive,
  wellnessAddCertificate,
} from "../../endpoints/API";

const { Text } = Typography;

// Breakpoint hook
function useBreakpoint() {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return {
    isMobile: width < 576,
    isTablet: width >= 576 && width < 992,
    isDesktop: width >= 992,
  };
}

const WellnessExecutive = () => {
  const user = useSelector((state) => state.user.user);
  const { isMobile, isTablet } = useBreakpoint();

  const [visible, setVisible] = useState(false);
  const [executives, setExecutives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingExecutive, setEditingExecutive] = useState(null);
  const [form] = Form.useForm();
  const [lab, setLab] = useState([]);
  const [assignLabModalVisible, setAssignLabModalVisible] = useState(false);
  const [selectedExecutive, setSelectedExecutive] = useState(null);
  const [selectedLabId, setSelectedLabId] = useState(null);
  const [isEditingLab, setIsEditingLab] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState(null);

  const baseUrl = useSelector(selectBaseUrl);
  const prodTestUrl = useSelector(selectProdTestUrl);

  useEffect(() => {
    const getExecutives = async () => {
      try {
        await fetchWellnessExecutive(setExecutives, baseUrl, prodTestUrl);
      } catch (error) {
        message.error(
          error?.response?.data?.error || "Failed to fetch executives."
        );
      }
    };
    getExecutives();
  }, []);

  useEffect(() => {
    const getGlobalData = async () => {
      try {
        await fetchLab(setLab, baseUrl, prodTestUrl);
      } catch (error) {
        message.error(
          error?.response?.data?.error || "Failed to fetch lab data."
        );
      }
    };
    getGlobalData();
  }, []);

  const handleCreateClick = () => {
    setEditingExecutive(null);
    form.resetFields();
    setVisible(true);
  };

  const handleAssignLabClick = (record, isEdit = false) => {
    setSelectedExecutive(record);
    setSelectedLabId(record.labs?.[0]?.id || null);
    setIsEditingLab(isEdit);
    setAssignLabModalVisible(true);
  };

  const handleAssignLabSubmit = async () => {
    if (!selectedLabId) {
      message.error("Please select a lab.");
      return;
    }
    try {
      let response;
      if (isEditingLab) {
        response = await update_assign_lab_to_we({
          wellnessExecutiveId: selectedExecutive.id,
          labId: selectedLabId,
        });
      } else {
        response = await assign_lab_to_we({
          wellnessExecutiveId: selectedExecutive.id,
          labId: selectedLabId,
        });
      }

      if (response.statusText == "OK") {
        message.success(
          isEditingLab ? "Lab updated successfully!" : "Lab assigned successfully!"
        );
        setAssignLabModalVisible(false);
        await fetchWellnessExecutive(setExecutives, baseUrl, prodTestUrl);
      } else {
        message.error(
          isEditingLab
            ? "Failed to update lab."
            : "This Lab is already assigned to another Wellness Executive."
        );
      }
    } catch (error) {
      message.error(
        error?.response?.data?.error ||
          (isEditingLab
            ? "Error updating lab."
            : "This Lab is already assigned to another Wellness Executive.")
      );
    }
  };

  const handleEditClick = (record) => {
    setEditingExecutive(record);
    form.setFieldsValue({
      firstName: record.first_name,
      middleName: record.middle_name,
      lastName: record.last_name,
      phone: record.phone,
      email: record.email,
      lab: record?.labs?.id,
      role: record.role || null,
    });
    setVisible(true);
  };

  const onFinish = async (values) => {
    setLoading(true);
    const payload = { ...values, id: editingExecutive?.id };
    console.log(payload);
    try {
      let response;
      if (editingExecutive) {
        response = await updateExecutive(payload);
      } else {
        response = await createExecutive(payload);
      }

      if (response.statusText == "OK") {
        message.success(
          editingExecutive
            ? "Wellness Executive updated successfully!"
            : "Wellness Executive created successfully!"
        );
        setVisible(false);
        await fetchWellnessExecutive(setExecutives, baseUrl, prodTestUrl);
      } else {
        message.error(
          editingExecutive
            ? "Failed to update Wellness Executive."
            : "Failed to create Wellness Executive or Wellness Executive with this number already exist."
        );
      }
    } catch (error) {
      message.error(
        error?.response?.data?.error ||
          (editingExecutive
            ? "Error updating Wellness Executive."
            : "Error creating Wellness Executive.")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (record, checked) => {
    const newStatus = checked ? "enable" : "disable";
    try {
      const response = await toggleExecutiveStatus(record.id, newStatus);
      if (response.statusText == "OK") {
        message.success(`Status updated to ${newStatus}`);
        await fetchWellnessExecutive(setExecutives, baseUrl, prodTestUrl);
      } else {
        message.error("Failed to update status.");
      }
    } catch (error) {
      message.error(error?.response?.data?.error || "Error updating status.");
    }
  };

  const handleCancel = () => {
    setVisible(false);
    form.resetFields();
  };

  // ── Certificate upload cell (reused in both card and table) ──
  const CertificateCell = ({ record }) => {
    const certificatePath = record?.certificate;
    if (certificatePath) {
      return (
        <SecureImage
          key={`cert-${record.id}`}
          path={`${certificatePath}`}
          width={80}
        />
      );
    }
    return (
      <Upload
        name="certificate"
        accept="image/*"
        maxCount={1}
        showUploadList={false}
        customRequest={async ({ file, onSuccess, onError }) => {
          const formData = new FormData();
          formData.append("certificate", file);
          formData.append("id", record.id);
          try {
            const response = await wellnessAddCertificate(formData);
            if (response.statusText == "OK") {
              message.success("Certificate uploaded successfully!");
              onSuccess("ok");
              await fetchWellnessExecutive(setExecutives, baseUrl, prodTestUrl);
            } else {
              const errorText = await response.text();
              message.error(`Upload failed: ${errorText}`);
              onError(new Error(errorText));
            }
          } catch (error) {
            message.error(
              error?.response?.data?.error || "Error uploading certificate."
            );
            onError(error);
          }
        }}
      >
        <Button type="link" size="small" icon={<UploadOutlined />}>
          Upload
        </Button>
      </Upload>
    );
  };

  // ── Mobile card for each executive ──
  const ExecutiveCard = ({ record }) => {
    const fullName = `${record.first_name} ${record.middle_name || ""} ${record.last_name}`.trim();
    const isEnabled = record.status === "enable";

    return (
      <Card
        size="small"
        style={{ marginBottom: 12, borderRadius: 8 }}
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
            <Text strong style={{ fontSize: 14 }}>{fullName}</Text>
            <Switch
              checked={isEnabled}
              checkedChildren="Enabled"
              unCheckedChildren="Disabled"
              onChange={(checked) => handleStatusChange(record, checked)}
              size="small"
            />
          </div>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {/* Phone */}
          <div style={{ display: "flex", gap: 8 }}>
            <Text type="secondary" style={{ fontSize: 12, minWidth: 65 }}>Phone:</Text>
            <Text style={{ fontSize: 12 }}>{record.phone || "-"}</Text>
          </div>

          {/* Email */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Text type="secondary" style={{ fontSize: 12, minWidth: 65 }}>Email:</Text>
            <Text style={{ fontSize: 12, wordBreak: "break-all" }}>{record.email || "-"}</Text>
          </div>

          {/* Lab */}
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <Text type="secondary" style={{ fontSize: 12, minWidth: 65 }}>Lab:</Text>
            {record.labs ? (
              <Text style={{ fontSize: 12 }}>{record.labs.name}</Text>
            ) : (
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Tag style={{ margin: 0 }}>Not Assigned</Tag>
                <Tooltip title="Click on Edit to assign lab.">
                  <InfoCircleOutlined style={{ fontSize: 12, color: "#aaa" }} />
                </Tooltip>
              </span>
            )}
          </div>

          {/* Certificate */}
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Text type="secondary" style={{ fontSize: 12, minWidth: 65 }}>Certificate:</Text>
            <CertificateCell record={record} />
          </div>

          {/* Role */}
          {record.role && (
            <div style={{ display: "flex", gap: 8 }}>
              <Text type="secondary" style={{ fontSize: 12, minWidth: 65 }}>Role:</Text>
              <Text style={{ fontSize: 12 }}>{record.role}</Text>
            </div>
          )}

          {/* Edit button */}
          <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px solid #f0f0f0" }}>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditClick(record)}
            >
              Edit
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  // ── Desktop/Tablet table columns ──
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => {
        const fullName = `${record.first_name} ${record.middle_name || ""} ${record.last_name}`;
        return fullName.trim();
      },
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (text) => text || "-",
    },
    {
      title: "Lab",
      dataIndex: "labs",
      key: "labs",
      render: (labs, record) => {
        if (labs) {
          return labs.name;
        }
        return (
          <>
            <Tag>Not Assigned</Tag>{" "}
            <Tooltip title="Click on Edit to assign lab.">
              <InfoCircleOutlined />
            </Tooltip>
          </>
        );
      },
    },
    {
      title: "Certificate",
      key: "certificate",
      render: (text, record) => <CertificateCell record={record} />,
    },
    {
      title: "Edit",
      key: "edit",
      render: (text, record) => (
        <Button onClick={() => handleEditClick(record)}>
          <EditOutlined />
        </Button>
      ),
    },
    {
      title: "Status",
      key: "status",
      render: (text, record) => (
        <Switch
          checked={record.status === "enable"}
          checkedChildren="Enabled"
          unCheckedChildren="Disabled"
          onChange={(checked) => handleStatusChange(record, checked)}
        />
      ),
    },
  ];

  return (
    <>
      {/* Header row */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 16,
        }}
      >
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreateClick}
        >
          {isMobile ? "Create WE" : "Create Wellness Executive"}
        </Button>
      </div>

      {/* Mobile: Card list | Tablet/Desktop: Table */}
      {isMobile ? (
        <div>
          {executives.length === 0 ? (
            <div style={{ textAlign: "center", padding: 32, color: "#999" }}>
              No wellness executives found.
            </div>
          ) : (
            executives.map((record) => (
              <ExecutiveCard
                key={record.id || record.email}
                record={record}
              />
            ))
          )}
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <Table
            columns={columns}
            dataSource={executives}
            rowKey={(record) => record.id || record.email}
            size={isTablet ? "small" : "middle"}
            scroll={{ x: isTablet ? 700 : undefined }}
          />
        </div>
      )}

      {/* Create / Edit Executive Modal */}
      <Modal
        title={
          editingExecutive
            ? "Update Wellness Executive"
            : "Create Wellness Executive"
        }
        open={visible}
        onCancel={handleCancel}
        footer={null}
        width={isMobile ? "95vw" : 520}
        centered={isMobile}
        destroyOnClose
      >
        <Form layout="vertical" onFinish={onFinish} form={form}>
          <Form.Item
            name="firstName"
            label="First Name"
            rules={[{ required: true, message: "Please enter first name!" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="middleName" label="Middle Name">
            <Input />
          </Form.Item>

          <Form.Item
            name="lastName"
            label="Last Name"
            rules={[{ required: true, message: "Please enter last name!" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Phone"
            rules={[{ required: true, message: "Please enter phone number!" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="email" label="Email">
            <Input />
          </Form.Item>

          <Form.Item label="Select Lab" name="lab">
            <Select
              placeholder="Select a lab"
              value={selectedLabId}
              onChange={(value) => setSelectedLabId(value)}
            >
              {lab.map((l) => (
                <Select.Option key={l.id} value={l.id}>
                  {l.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Select Role" name="role">
            <Select placeholder="Select a role">
              <Select.Option value="wellness_executive">
                Wellness Executive
              </Select.Option>
              <Select.Option value="relay">Relay</Select.Option>
            </Select>
          </Form.Item>

          <Space>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {editingExecutive ? "Update" : "Submit"}
            </Button>
          </Space>
        </Form>
      </Modal>

      {/* Assign / Update Lab Modal */}
      <Modal
        title={isEditingLab ? "Update Lab" : "Assign Lab"}
        open={assignLabModalVisible}
        onCancel={() => setAssignLabModalVisible(false)}
        onOk={handleAssignLabSubmit}
        okText={isEditingLab ? "Update" : "Submit"}
        width={isMobile ? "95vw" : 480}
        centered={isMobile}
      >
        <Form layout="vertical">
          <Form.Item label="Select Lab" required>
            <Select
              placeholder="Select a lab"
              value={selectedLabId}
              onChange={(value) => setSelectedLabId(value)}
              style={{ width: "100%" }}
            >
              {lab.map((l) => (
                <Select.Option key={l.id} value={l.id}>
                  {l.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Certificate image preview Modal */}
      <Modal
        open={isModalVisible}
        footer={null}
        onCancel={() => {
          setIsModalVisible(false);
          console.log("hfgdshgfs", modalImageUrl);
          if (modalImageUrl) {
            window.URL.revokeObjectURL(modalImageUrl);
          }
        }}
        width={isMobile ? "95vw" : 600}
        centered={isMobile}
      >
        {modalImageUrl && (
          <img
            src={modalImageUrl}
            alt="Certificate"
            style={{ width: "100%", maxHeight: "70vh", objectFit: "contain" }}
          />
        )}
      </Modal>
    </>
  );
};

export default WellnessExecutive;